import supabase from './Supabase';
import CryptoJS from 'crypto-js';

import { showToast } from "./Toasts";
import { getUserInfo } from './UserUtil';
import { getPreviewByUser } from "./Database";
import useStorageStore from '../stores/Storage';

const inFlightRequests: Record<string, Promise<string | Blob | null>> = {};

export function generateSafeFilename(filename: string, addDateTime = false) {
    const nameParts = filename.split('.')
    const ext = nameParts.pop() || 'pdf'
    const name = nameParts.join('.')

    let sanitizedName = name
        .normalize('NFD') // Normalize unicode (convert accents to ASCII equivalents where possible)
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (e.g. é -> e)
        // Normalize common Unicode dashes to a standard hyphen
        // Range U+2010–U+2015 covers hyphen, non‑breaking hyphen, figure dash, en/em dash, horizontal bar
        .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
        // eslint-disable-next-line no-control-regex
        .replace(/[<>:"/\\|?*\x00-\x1f#%&{}[\]+`^~'@=;]/g, '-') // Windows forbidden + control chars + Supabase problematic chars
        .replace(/\s+/g, '-') // Replace spaces with hyphens (e.g. "hello world" -> "hello-world")
        .replace(/_-+|-+_/g, '_') // Remove hyphens when mixed with underscores, keep underscore
        .replace(/-+/g, '-') // Collapse multiple hyphens
        .replace(/_+/g, '_') // Collapse multiple underscores
        .replace(/^[-_]+|[-_]+$/g, '') // Trim separators from start and end
        .replace(/^\./g, '') // Remove leading dots
        .replace(/,/g, '') // Remove commas
        .trim() // Trim whitespace, just in case

    if (addDateTime) {
        const now = new Date();
        const datetimeStr = now.toISOString().replace(/[:.]/g, '-'); // e.g. 2023-08-30T14-23-45-678Z
        sanitizedName += `_${datetimeStr}`;
    }

    const maxLength = 255 - ext.length - 1 // Limit filename length to 255 chars including extension
    const truncated = sanitizedName.slice(0, maxLength)

    return `${truncated || 'untitled'}.${ext}`
}

/**
 * Gets the MD5 hash of a file in the 'files' bucket.
 * @param path The path of the file in the bucket.
 * @returns Promise<string> The MD5 hash as a hex string.
 */
export async function getFileMD5(path: string): Promise<string> {
    const { data, error } = await supabase.storage
        .from('files')
        .download(path);

    if (error || !data) {
        showToast("error", `Failed to download file: ${error?.message}`);
    }

    const buffer = await data.arrayBuffer();
    // Convert ArrayBuffer to WordArray for crypto-js
    const wordArray = CryptoJS.lib.WordArray.create(buffer);
    const hash = CryptoJS.MD5(wordArray).toString();
    return hash;
}

/**
 * Gets the MIME type of a file in the 'files' bucket.
 * @param path The path of the file in the bucket.
 * @returns Promise<string | null> The MIME type of the file, or null if not found.
 */
export async function getFileMimeType(path: string, fileID: string = null): Promise<string | null> {
    if (fileID) {
        const { data, error } = await supabase
            .from('files')
            .select('Type')
            .eq('file_id', fileID)
            .single();

        if (error || !data) {
            console.warn(`Unable to get file type from database: ${error?.message}`);
        }

        if (data && data.Type) {
            const mimeType = data.Type === "PDF" ? "application/pdf"
                : data.Type === "Document" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                : data.Type === "Spreadsheet" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : data.Type
            return mimeType;
        }
    }

    // Get mimetype from the storage metadata
    const { data: storageData, error: storageError } = await supabase.storage
        .from('files')
        .list('', { search: path });

    if (storageError || !storageData || storageData.length === 0) {
        return 'application/pdf'; // Default to PDF if we can't find the file or its metadata
    }

    // Find the file by path (Supabase list returns array of file objects)
    const file = storageData.find((f) => f.name === path || f.name.endsWith('/' + path));
    return file?.metadata?.mimetype ?? 'application/pdf';
}

export async function storageProxy(filename: string, returnType: string = "url", timeToLive: number = 0, purpose: "preview" | "download" = "preview") {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return null;

    const removed = useStorageStore.getState().removeExpiredURLs;
    if (removed) removed(); // Clean up expired URLs on each call

    const signedURLs = useStorageStore.getState().signedURLs;
    const setSignedURLs = useStorageStore.getState().setSignedURLs;

    // Cache key includes purpose so a cached preview URL can't be silently reused to
    // serve (and skip recording) a download, or vice versa.
    const cacheKey = `${purpose}:${filename}`;

    if (signedURLs[cacheKey]) {
        if (returnType === "url") {
            return signedURLs[cacheKey].url;
        } else if (returnType === "blob") {
            try {
                const blobResponse = await fetch(signedURLs[cacheKey].url);
                if (!blobResponse.ok) return null;
                return await blobResponse.blob();
            } catch {
                return null;
            }
        }
    }

    // If there's already an in-flight request for this filename/purpose, return the existing promise
    if (inFlightRequests[cacheKey]) {
        return inFlightRequests[cacheKey];
    }

    const supabaseUrl = process.env.REACT_APP_SUPABASE_ENDPOINT;
    const encodedPath = filename.split('/').map(encodeURIComponent).join('/');
    const proxyUrl = `${supabaseUrl}/functions/v1/storage-proxy/files/${encodedPath}`;

    const request = fetch(proxyUrl, {
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            'x-ttl': timeToLive.toString(),
            'x-request-purpose': purpose,
        },
    })
        .then(async (response) => {
            if (!response.ok) return null;

            const { url } = await response.json();
            if (typeof url !== 'string' || !url || !supabaseUrl) return null;

            const resolvedUrl = new URL(url, supabaseUrl).toString();

            setSignedURLs({
                ...useStorageStore.getState().signedURLs,
                [cacheKey]: {
                    url: resolvedUrl,
                    created: new Date().toISOString(),
                    ttl: timeToLive
                }
            });

            if (returnType === "url") {
                return resolvedUrl;
            } else if (returnType === "blob") {
                try {
                    const blobResponse = await fetch(resolvedUrl);
                    if (!blobResponse.ok) return null;
                    return await blobResponse.blob();
                } catch {
                    return null;
                }
            }
        })
        .finally(() => {
            delete inFlightRequests[cacheKey];
        });

    inFlightRequests[cacheKey] = request;
    return request;
}

export async function getIndexerSignedURL(filename: string, user_id: string) {
    const userInfo = await getUserInfo(user_id);

    if (!userInfo || (!userInfo.isIndexer && !userInfo.isApprover && !userInfo.isCurator)) {
        return null;
    }

    const { data } = await supabase.storage
        .from("files")
        .createSignedUrl(filename, 600);

    return (data?.signedUrl || null);
}

export async function getPreviewSignedURL(filename: string, file_id: string, user_id: string, timeToLive: number = 300) {
    const userPreview = await getPreviewByUser(file_id, user_id);
    if (!userPreview) return null;

    const url = await storageProxy(filename, "url", timeToLive, "preview");
    return url || null;
}

export async function getAllFiles(setStatus, limit = 1000) {
    const allFiles = [];
    let offset = 0;
    
    while (true) {
        const { data, error } = await supabase.storage
            .from("files")
            .list("", {
                limit,
                offset,
                sortBy: { column: "name", order: "asc" }
            });

        if (error) {
            console.error("Error fetching files:", error);
            return null;
        }

        if (!data?.length) break;

        // Filter out files with names starting with '.'
        const filteredData = data.filter(file =>
            !file.name.startsWith('.') &&
            !file.name.startsWith('_deleted'));
        allFiles.push(...filteredData);
        setStatus(`Found ${allFiles.length} files in storage...`);

        if (data.length < limit) break;

        offset += limit;
    }

    return allFiles;
}

export async function uploadFile(bucket: string, file: File, replaceExisting: boolean = false) {
    const safeName = generateSafeFilename(file.name);

    const { data, error } = await supabase.storage
        .from(bucket)
        .upload(safeName, file, { upsert: replaceExisting });

    if (error) {
        if (error.message.includes("The resource already exists")) {
            showToast("error", `${safeName} already exists. Please use a different filename.`);
        } else {
            showToast("error", `Unable to upload ${safeName}: ${error.message}`);
        }
    }

    return data;
}

export async function softDeleteFile(bucket: string, fileName: string, silent = false) {
    const { data: existsData, error: existsError } = await supabase
        .storage
        .from(bucket)
        .exists(fileName);

    if (!existsData || existsError) {
        // The file doesn't exist, so there's nothing to delete
        return true;
    }

    const { data, error } = await supabase.storage
        .from(bucket)
        .move(fileName, `_deleted/${fileName}`);

    if (error) {
        if (error.message.includes("The resource already exists")) {
            const fileWithDate = generateSafeFilename(fileName, true);

            const { data, error } = await supabase.storage
                .from(bucket)
                .move(fileName, `_deleted/${fileWithDate}`);

            if (error) {
                if (!silent) {
                    showToast("error", `Unable to delete ${fileName}: ${error.message}`);
                }
                return false;
            }
            return data;
        }
        if (!silent) {
            showToast("error", `Unable to delete ${fileName}: ${error.message}`);
        }
        return false;
    }

    return data;
}